import Vue from 'vue'
import VueRouter from 'vue-router'
import HomeView from '@/views/HomeView.vue'
import { API_ROUTES } from '@/axios'
import ProvesControl from '@/views/ProvesControl.vue'

Vue.use(VueRouter)

export const checkToken = async () => {
  const token = localStorage.getItem('token')
  if (!token) {
    const currentPath = window.location.pathname + window.location.search
    if (!currentPath.startsWith('/login')) {
      localStorage.setItem('redirectAfterLogin', currentPath)
    }
    return false
  }

  try {
    const response = await fetch(API_ROUTES.ME, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })

    if (!response.ok) {
      const currentPath = window.location.pathname + window.location.search
      if (!currentPath.startsWith('/login')) {
        localStorage.setItem('redirectAfterLogin', currentPath)
      }
    }

    return response.ok
  } catch (error) {
    const currentPath = window.location.pathname + window.location.search
    if (!currentPath.startsWith('/login')) {
      localStorage.setItem('redirectAfterLogin', currentPath)
    }
    return false
  }
}

const checkTokenMiddleware = async (to, from, next) => {
  const isValid = await checkToken()
  if (isValid) {
    next()
  } else {
    localStorage.removeItem('token')
    next('/login')
  }
}

const checkPermission = async (to, from, next) => {
  const isValid = await checkToken()
  if (!isValid) {
    localStorage.removeItem('token')
    next('/login')
    return
  }
  try {
    const response = await fetch(
      API_ROUTES.CHECK_PERMISSION(to.meta.permission),
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    )

    const data = await response.json()
    if (data.has_permission) {
      next()
    } else {
      Vue.prototype.$buefy?.notification?.open({
        message: data.detail || 'No tienes permisos para acceder a esta página',
        type: 'is-danger',
        duration: 5000
      })
      next('/')
    }
  } catch (error) {
    next('/')
  }
}

const routes = [
  {
    meta: {
      title: 'Inici'
    },
    path: '/',
    name: 'home',
    component: HomeView,
    beforeEnter: checkTokenMiddleware
  },
  {
    meta: {
      title: 'Users',
      permission: 'superusuari'
    },
    path: '/users',
    name: 'users',
    component: () => import('@/views/UsersControl.vue'),
    beforeEnter: checkPermission
  },
  {
    meta: {
      title: 'Profile'
    },
    path: '/profile',
    name: 'profile',
    component: () => import('@/views/ProfileView.vue'),
    beforeEnter: checkTokenMiddleware
  },
  {
    meta: {
      title: 'Nou Usuari',
      permission: 'superusuari'
    },
    path: '/user/new',
    name: 'user.new',
    component: () => import('@/views/UserFormView.vue'),
    beforeEnter: checkPermission
  },
  {
    meta: {
      title: 'Editar Usuari',
      permission: 'superusuari'
    },
    path: '/user/:id',
    name: 'user.edit',
    component: () => import('@/views/UserFormView.vue'),
    props: true,
    beforeEnter: checkPermission
  },
  {
    meta: {
      title: 'config.titol',
      permission: 'superusuari'
    },
    path: '/config',
    name: 'config',
    component: () => import('@/views/ConfigView.vue'),
    beforeEnter: checkTokenMiddleware
  },
  {
    meta: {
      title: 'Dashboard',
      permission: 'dashboard_client'
    },
    path: '/xperienceiq/dashboard-client',
    name: 'dashboard-client',
    component: () => import('@/ExperienceIQ/client/views/DashboardClient.vue'),
    beforeEnter: checkPermission
  },
  {
    meta: {
      title: 'Clients',
      permission: 'superusuari'
    },
    path: '/clients',
    name: 'clients',
    component: () => import('@/ExperienceIQ/consola/views/ClientsControl.vue'),
    beforeEnter: checkPermission
  },
  {
    meta: {
      title: 'Monitoritzacio',
      permission: 'superusuari'
    },
    path: '/conexions',
    name: 'conexions',
    component: () => import('@/views/ConexionsView.vue'),
    beforeEnter: checkPermission
  },
  // Rutes per Informes de Seguiment
  {
    meta: {
      title: 'Informes de Seguiment'
    },
    path: '/xperienceiq/informes-seguiment',
    name: 'informes-seguiment',
    component: () => import('@/ExperienceIQ/client/views/InformesSeguiment.vue'),
    beforeEnter: checkTokenMiddleware
  },
  {
    meta: {
      title: 'Configuració Informes',
      permission: 'superusuari'
    },
    path: '/informes-seguiment/config',
    name: 'informes-seguiment.config',
    component: () => import('@/views/InformesConfig.vue'),
    beforeEnter: checkPermission
  },
  {
    meta: {
      title: 'Paràmetres Client',
      permission: 'superusuari'
    },
    path: '/informes-seguiment/parametres',
    name: 'informes-seguiment.parametres',
    component: () => import('@/ExperienceIQ/consola/views/ParametresClient.vue'),
    beforeEnter: checkPermission
  },
  {
    meta: {
      title: 'Login'
    },
    path: '/login',
    name: 'login',
    component: () => import('@/views/Login/LoginView.vue'),
    beforeEnter: async (to, from, next) => {
      // Si ya tiene token válido, redirigir a home
      const isValid = await checkToken()
      if (isValid) {
        next('/')
      } else {
        next()
      }
    }
  },
  {
    meta: {
      title: 'Proves',
      permission: 'veure_proves'
    },
    path: '/proves',
    name: 'ProvesControl',
    component: ProvesControl,
    beforeEnter: checkPermission
  },
  {
    meta: {
      title: 'Descargar App'
    },
    path: '/app-download',
    name: 'app-download',
    component: () => import('@/views/AppDownloadView.vue'),
    beforeEnter: checkTokenMiddleware
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFoundView.vue')
  }
]

const router = new VueRouter({
  mode: 'history',
  routes,
  base: '/',
  scrollBehavior (to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  }
})

// Limpiar parámetro 'client' de la URL si existe
router.beforeEach((to, from, next) => {
  if (to.query.client) {
    // Si hay parámetro client en la URL, eliminarlo
    const query = { ...to.query }
    delete query.client

    next({
      path: to.path,
      query,
      params: to.params,
      hash: to.hash,
      replace: true
    })
  } else {
    next()
  }
})

// Añadir manejo de errores global en el router
router.onError((error) => {
  console.error('Router error:', error)
  // Puedes añadir alguna notificación global aquí si es necesario
})

// Ensure the router instance is exported
export { router }

export default router
